import React, { useState, useRef, useCallback, useMemo } from 'react'
import { Currency } from '@sequencehq/api'
import { toMoney } from '@sequencehq/utils'

export type CellPosition = { row: number; col: number }

export type SelectionRange = {
  start: CellPosition
  end: CellPosition
}

export type ContextMenuState = {
  isOpen: boolean
  position: { x: number; y: number }
  selectionInfo?: {
    rowCount: number
    columnCount: number
    rowLabels: string[]
    columnLabels: string[]
    cellData: Array<{
      row: number
      col: number
      value: string
      rowLabel: string
      columnLabel: string
      rowData?: any
    }>
  }
}

export type SelectionBoundaries = {
  minRow: number
  maxRow: number
  minCol: number
  maxCol: number
  focusRow: number
  focusCol: number
  version: number // Incremented when selection changes
}

export const useTableSelection = (allRows?: any[]) => {
  const formatCurrency = useCallback(
    (value: number | undefined, currencyCode: string) => {
      if (typeof value !== 'number' || Number.isNaN(value)) {
        return '0.00'
      }

      return toMoney({
        value: value.toString(),
        currency: currencyCode as Currency
      })
    },
    []
  )

  // Version counter - increment to trigger re-renders only when selection changes
  // Quite clever solution from Claude!
  const [selectionVersion, setSelectionVersion] = useState(0)

  // Focus position (active cell with thick border)
  const [focusPosition, setFocusPosition] = useState<CellPosition>({
    row: -1,
    col: -1
  })

  const [selectedRange, setSelectedRange] = useState<SelectionRange | null>(
    null
  )

  const [contextMenu, setContextMenu] = useState<ContextMenuState>({
    isOpen: false,
    position: { x: 0, y: 0 }
  })

  // Drag selection state (doesn't need to trigger re-renders)
  const isDraggingRef = useRef(false)
  const dragStartRef = useRef<CellPosition | null>(null)
  const lastMouseEnterRef = useRef<CellPosition | null>(null)
  const rafRef = useRef<number | null>(null)

  // Track mousedown state for click vs drag detection
  const mouseDownInfoRef = useRef<{
    row: number
    col: number
    wasSelected1x1: boolean
    hasDragged: boolean
  } | null>(null)

  // Calculate min/max boundaries from selection range
  const selectionBoundaries = useMemo((): SelectionBoundaries => {
    if (!selectedRange) {
      return {
        minRow: -1,
        maxRow: -1,
        minCol: -1,
        maxCol: -1,
        focusRow: focusPosition.row,
        focusCol: focusPosition.col,
        version: selectionVersion
      }
    }

    return {
      minRow: Math.min(selectedRange.start.row, selectedRange.end.row),
      maxRow: Math.max(selectedRange.start.row, selectedRange.end.row),
      minCol: Math.min(selectedRange.start.col, selectedRange.end.col),
      maxCol: Math.max(selectedRange.start.col, selectedRange.end.col),
      focusRow: focusPosition.row,
      focusCol: focusPosition.col,
      version: selectionVersion
    }
  }, [selectedRange, focusPosition, selectionVersion])

  const isCellSelected = useCallback(
    (row: number, col: number): boolean => {
      const { minRow, maxRow, minCol, maxCol } = selectionBoundaries
      return (
        minRow >= 0 &&
        row >= minRow &&
        row <= maxRow &&
        col >= minCol &&
        col <= maxCol
      )
    },
    [selectionBoundaries]
  )

  const getSelectionBorders = useCallback(
    (row: number, col: number) => {
      if (!isCellSelected(row, col)) {
        return { top: false, right: false, bottom: false, left: false }
      }

      const { minRow, maxRow, minCol, maxCol } = selectionBoundaries

      return {
        top: row === minRow,
        right: col === maxCol,
        bottom: row === maxRow,
        left: col === minCol
      }
    },
    [selectionBoundaries, isCellSelected]
  )

  const updateSelection = useCallback(
    (newRange: SelectionRange | null, newFocus: CellPosition) => {
      setSelectedRange(newRange)
      setFocusPosition(newFocus)
      setSelectionVersion(v => v + 1) // Increment version to trigger memo checks
    },
    []
  )

  const handleCellClick = useCallback(
    (row: number, col: number, event: React.MouseEvent) => {
      event.preventDefault()

      // Shift-click extends selection from drag start
      if (event.shiftKey && dragStartRef.current) {
        updateSelection(
          {
            start: dragStartRef.current,
            end: { row, col }
          },
          { row, col }
        )
        return
      }

      // Check if clicking already-selected single cell → deselect
      const info = mouseDownInfoRef.current
      const shouldToggleOff =
        !!info &&
        info.wasSelected1x1 &&
        !info.hasDragged &&
        info.row === row &&
        info.col === col

      if (shouldToggleOff) {
        updateSelection(null, { row: -1, col: -1 })
        dragStartRef.current = null
        mouseDownInfoRef.current = null
        return
      }

      // Keep the mousedown-established selection
      mouseDownInfoRef.current = null
    },
    [updateSelection]
  )

  const handleCellMouseDown = useCallback(
    (row: number, col: number, event: React.MouseEvent) => {
      if (event.button !== 0) return // Only left click

      event.preventDefault()

      // Check if clicking already-selected single cell
      const wasSelected1x1 =
        !!selectedRange &&
        selectedRange.start.row === selectedRange.end.row &&
        selectedRange.start.col === selectedRange.end.col &&
        selectedRange.start.row === row &&
        selectedRange.start.col === col

      mouseDownInfoRef.current = {
        row,
        col,
        wasSelected1x1,
        hasDragged: false
      }

      // Start drag selection
      isDraggingRef.current = true
      dragStartRef.current = { row, col }
      lastMouseEnterRef.current = { row, col }

      React.startTransition(() => {
        updateSelection(
          {
            start: { row, col },
            end: { row, col }
          },
          { row, col }
        )
      })
    },
    [selectedRange, updateSelection]
  )

  /**
   * Mouse enter handler - extends selection during drag
   * Uses RAF (requestAnimationFrame) to throttle updates for performance
   */
  const handleCellMouseEnter = useCallback(
    (row: number, col: number) => {
      if (!isDraggingRef.current || !dragStartRef.current) return

      // Deduplicate - don't process same cell twice
      const last = lastMouseEnterRef.current
      if (last && last.row === row && last.col === col) return

      lastMouseEnterRef.current = { row, col }

      // Mark as drag if moving beyond initial cell
      if (mouseDownInfoRef.current) {
        const start = mouseDownInfoRef.current
        if (start.row !== row || start.col !== col) {
          mouseDownInfoRef.current.hasDragged = true
        }
      }

      // Throttle updates with RAF for smooth performance
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
      }

      rafRef.current = requestAnimationFrame(() => {
        updateSelection(
          {
            start: dragStartRef.current!,
            end: { row, col }
          },
          { row, col }
        )
        rafRef.current = null
      })
    },
    [updateSelection]
  )

  // This will for now only be used when chatty revrec is enabled and we enable the context menu
  const handleCellRightClick = useCallback(
    (
      row: number,
      col: number,
      event: React.MouseEvent,
      data: any,
      currency: string
    ) => {
      event.preventDefault()

      // Close any existing context menu
      setContextMenu({ isOpen: false, position: { x: 0, y: 0 } })

      // If no selection or right-clicking outside selection, select the clicked cell
      if (!selectedRange || !isCellSelected(row, col)) {
        updateSelection(
          {
            start: { row, col },
            end: { row, col }
          },
          { row, col }
        )
        dragStartRef.current = { row, col }
      }

      // Generate selection info for context menu
      const currentSelection =
        selectedRange && isCellSelected(row, col)
          ? selectedRange
          : { start: { row, col }, end: { row, col } }

      const minRow = Math.min(
        currentSelection.start.row,
        currentSelection.end.row
      )
      const maxRow = Math.max(
        currentSelection.start.row,
        currentSelection.end.row
      )
      const minCol = Math.min(
        currentSelection.start.col,
        currentSelection.end.col
      )
      const maxCol = Math.max(
        currentSelection.start.col,
        currentSelection.end.col
      )

      // Extract selection information
      const cellData: Array<{
        row: number
        col: number
        value: string
        rowLabel: string
        columnLabel: string
        rowData?: any
      }> = []

      // Get column labels
      const getColumnLabel = (colIndex: number) => {
        if (colIndex === 0) return 'Customer/Group'
        if (colIndex === 1) return 'Total Booked'
        if (
          colIndex >= 2 &&
          colIndex < 2 + (data.monthlyColumns?.length || 0)
        ) {
          return data.monthlyColumns[colIndex - 2] || `Month ${colIndex - 1}`
        }
        if (colIndex === 2 + (data.monthlyColumns?.length || 0))
          return 'Recognized'
        if (colIndex === 3 + (data.monthlyColumns?.length || 0))
          return 'Remaining'
        return `Column ${colIndex}`
      }

      // Get row label and cell value
      const getRowInfo = (rowIndex: number) => {
        // Use allRows if available (accounts for expansion state), fallback to data.rows
        const rowData =
          allRows?.[rowIndex]?.original || data.flatRows?.[rowIndex]
        return {
          label: rowData?.label || `Row ${rowIndex}`,
          data: rowData
        }
      }

      // Collect unique row and column labels
      const uniqueRowLabels = new Set<string>()
      const uniqueColumnLabels = new Set<string>()

      for (let r = minRow; r <= maxRow; r++) {
        for (let c = minCol; c <= maxCol; c++) {
          const rowInfo = getRowInfo(r)
          const columnLabel = getColumnLabel(c)

          uniqueRowLabels.add(rowInfo.label)
          uniqueColumnLabels.add(columnLabel)

          // Get cell value
          let cellValue = ''
          if (c === 0) {
            cellValue = rowInfo.label
          } else if (c === 1) {
            cellValue = formatCurrency(rowInfo.data?.bookedTotal, currency)
          } else if (c >= 2 && c < 2 + (data.monthlyColumns?.length || 0)) {
            const monthKey = data.monthlyColumns[c - 2]
            cellValue = formatCurrency(
              rowInfo.data?.monthlyValues?.[monthKey],
              currency
            )
          } else if (c === 2 + (data.monthlyColumns?.length || 0)) {
            cellValue = formatCurrency(rowInfo.data?.recognized, currency)
          } else if (c === 3 + (data.monthlyColumns?.length || 0)) {
            cellValue = formatCurrency(rowInfo.data?.remaining, currency)
          }

          cellData.push({
            row: r,
            col: c,
            value: cellValue,
            rowLabel: rowInfo.label,
            columnLabel,
            rowData: rowInfo.data
          })
        }
      }

      const selectionInfo = {
        rowCount: maxRow - minRow + 1,
        columnCount: maxCol - minCol + 1,
        rowLabels: Array.from(uniqueRowLabels),
        columnLabels: Array.from(uniqueColumnLabels),
        cellData
      }

      // Show context menu
      setContextMenu({
        isOpen: true,
        position: { x: event.clientX, y: event.clientY },
        selectionInfo
      })
    },
    [selectedRange, isCellSelected, updateSelection, formatCurrency]
  )

  /**
   * Mouse up handler - ends drag selection
   * NO useEffect! Attach/detach in parent component or use onMouseUp
   */
  const handleMouseUp = useCallback(() => {
    isDraggingRef.current = false
    lastMouseEnterRef.current = null
    // Don't clear mouseDownInfoRef yet - needed for click handler

    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
  }, [])

  // Attach global mouseup listener (only way to catch mouseup outside table)
  React.useEffect(() => {
    document.addEventListener('mouseup', handleMouseUp)
    return () => document.removeEventListener('mouseup', handleMouseUp)
  }, [handleMouseUp])

  const closeContextMenu = useCallback(() => {
    setContextMenu({ isOpen: false, position: { x: 0, y: 0 } })
  }, [])

  const clearSelection = useCallback(() => {
    updateSelection(null, { row: -1, col: -1 })
    dragStartRef.current = null
  }, [updateSelection])

  return {
    selectionBoundaries,
    selectedRange,
    isDragging: isDraggingRef.current,
    currentPosition: focusPosition,
    setCurrentPosition: setFocusPosition,
    selectionStart: dragStartRef.current,
    setSelectionStart: (pos: CellPosition | null) => {
      dragStartRef.current = pos
    },
    setSelectedRange: (range: SelectionRange | null) => {
      const focus = range ? range.end : { row: -1, col: -1 }
      updateSelection(range, focus)
    },
    isCellSelected,
    getSelectionBorders,
    handleCellClick,
    handleCellMouseDown,
    handleCellMouseEnter,
    handleCellRightClick,
    clearSelection,
    contextMenu,
    closeContextMenu
  }
}
